"use client";

import React, { useState, useCallback, useMemo } from "react";
import Image from "next/image";
import { Store } from "lucide-react";
import { getLgsLogoUrl, cn } from "utils/utils";

/** xs–xl are square, on the daisyUI size scale; banner is the wide sidebar slot. */
type LogoSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "banner";

interface LgsLogoProps {
  wotcId: number;
  storeName: string;
  /** Only trading hubs have logos on the CDN. Falsy = icon fallback only, never probe. */
  isTradingHub?: boolean;
  size: LogoSize;
  className?: string;
  /** Classes for the fallback <Store/> icon; defaults to text-primary. */
  fallbackClassName?: string;
  priority?: boolean;
}

// Small sizes pad the image so wide (wordmark) logos sit whole inside the
// rounded-full box instead of having their corners clipped by the circle.
export const SIZE_CONFIG = {
  xs: {
    width: 20,
    height: 20,
    className: "h-5 w-5",
    pad: "p-0.5",
    iconSize: 18,
  },
  sm: { width: 32, height: 32, className: "h-8 w-8", pad: "p-1", iconSize: 18 },
  md: {
    width: 40,
    height: 40,
    className: "h-10 w-10",
    pad: "p-1",
    iconSize: 20,
  },
  lg: {
    width: 48,
    height: 48,
    className: "h-12 w-12",
    pad: "p-1.5",
    iconSize: 24,
  },
  xl: {
    width: 64,
    height: 64,
    className: "h-16 w-16",
    pad: "p-2",
    iconSize: 32,
  },
  "2xl": {
    width: 80,
    height: 80,
    className: "h-20 w-20",
    pad: "p-2",
    iconSize: 40,
  },
  banner: {
    width: 200,
    height: 96,
    className: "h-24 w-full",
    pad: "",
    iconSize: 40,
  },
} as const;

// Boxes this size and under ask the CDN for the hand-curated compact mark
// (/small/ prefix); a wordmark clipped into a 40px circle is unreadable.
const SMALL_SIZES: ReadonlySet<LogoSize> = new Set(["xs", "sm", "md"]);

function buildSources(
  wotcId: number,
  variant: "light" | "dark",
  small: boolean
): string[] {
  // The CDN Worker resolves small → themed → plain itself, so one request
  // covers every variant in every environment.
  const cdn = getLgsLogoUrl(wotcId, variant, small);
  return cdn ? [cdn] : [];
}

function FallbackIcon({
  size,
  className,
  fallbackClassName,
}: Pick<LgsLogoProps, "size" | "className" | "fallbackClassName">) {
  const { className: sizeClass, iconSize } = SIZE_CONFIG[size];
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center",
        sizeClass,
        className
      )}
    >
      <Store
        size={iconSize}
        className={fallbackClassName ?? "text-primary"}
        aria-hidden="true"
      />
    </span>
  );
}

interface VariantImageProps {
  wotcId: number;
  variant: "light" | "dark";
  storeName: string;
  size: LogoSize;
  className?: string;
  fallbackClassName?: string;
  priority?: boolean;
}

function VariantImage({
  wotcId,
  variant,
  storeName,
  size,
  className,
  fallbackClassName,
  priority,
}: VariantImageProps) {
  const small = SMALL_SIZES.has(size);
  const sources = useMemo(
    () => buildSources(wotcId, variant, small),
    [wotcId, variant, small]
  );
  const [sourceIndex, setSourceIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const handleError = useCallback(() => {
    setLoaded(false);
    setSourceIndex((prev) => prev + 1);
  }, []);
  const handleLoad = useCallback(() => setLoaded(true), []);

  if (sourceIndex >= sources.length) {
    return (
      <FallbackIcon
        size={size}
        className={className}
        fallbackClassName={fallbackClassName}
      />
    );
  }

  const { width, height, className: sizeClass, pad } = SIZE_CONFIG[size];

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden",
        sizeClass,
        pad,
        size === "banner" ? "rounded-lg" : "rounded-full",
        className
      )}
    >
      {!loaded && (
        <span className="skeleton absolute inset-0" aria-hidden="true" />
      )}
      <Image
        key={sourceIndex}
        className={cn(
          "h-full w-full object-contain transition-opacity duration-150",
          loaded ? "opacity-100" : "opacity-0"
        )}
        src={sources[sourceIndex]}
        alt={`${storeName} logo`}
        width={width}
        height={height}
        priority={priority}
        unoptimized={sources[sourceIndex].startsWith("http")}
        onError={handleError}
        onLoad={handleLoad}
      />
    </div>
  );
}

export function LgsLogo({
  wotcId,
  storeName,
  isTradingHub,
  size,
  className,
  fallbackClassName,
  priority,
}: LgsLogoProps) {
  if (!isTradingHub) {
    return (
      <FallbackIcon
        size={size}
        className={className}
        fallbackClassName={fallbackClassName}
      />
    );
  }

  // Sizes on the same side of SMALL_SIZES fetch the same URL, so a resize
  // across that line is the only thing that should restart the source chain.
  const variantKey = `${wotcId}:${SMALL_SIZES.has(size) ? "small" : "full"}`;
  return (
    <>
      <div className="contents dark:hidden">
        <VariantImage
          key={variantKey}
          wotcId={wotcId}
          variant="light"
          storeName={storeName}
          size={size}
          className={className}
          fallbackClassName={fallbackClassName}
          priority={priority}
        />
      </div>
      <div className="hidden dark:contents">
        <VariantImage
          key={variantKey}
          wotcId={wotcId}
          variant="dark"
          storeName={storeName}
          size={size}
          className={className}
          fallbackClassName={fallbackClassName}
        />
      </div>
    </>
  );
}
